import React, { useCallback, useMemo, useRef } from "react";

import clsx from "clsx";

import { parseDate } from "@/utils/formatters";

import { pan, recenter, resizeLeft, resizeRight, type Span } from "./brushMath";
import type { Point } from "./types";

export type OverviewBrushProps = {
  /** Coarse history sparkline (downsampled via bucketToTarget). */
  series: Point[];
  /** Optional coarse forecast tail, drawn lighter (item detail). */
  forecastSeries?: Point[];
  /** History/forecast boundary in epoch ms; draws a subtle divider when set. */
  nowMs?: number;
  viewRange: Span;
  onViewRangeChange: (range: Span) => void;
  /** Double-click handler (reset to the active preset). */
  onReset?: () => void;
  /** Full-span upper bound in epoch ms (forecast end). Keeps handle math aligned with the chart x-axis. */
  rangeMax?: number;
};

type DragMode = "pan" | "resize-left" | "resize-right";

const SVG_W = 100;
const SVG_H = 40;
const PAD_Y = 6;

export function OverviewBrush({
  series,
  forecastSeries = [],
  nowMs,
  viewRange,
  onViewRangeChange,
  onReset,
  rangeMax,
}: OverviewBrushProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // ── Data span (forecast tail extends the right edge when present) ──────────
  const all = [...series, ...forecastSeries];
  const times = all.map((p) => parseDate(p.x).getTime());
  const dataMin = times.length > 0 ? Math.min(...times) : 0;
  const seriesMax = times.length > 0 ? Math.max(...times) : 1;
  const dataMax = Math.max(seriesMax, rangeMax ?? -Infinity) || seriesMax;
  const data = useMemo<Span>(
    () => ({ min: dataMin, max: dataMax }),
    [dataMin, dataMax],
  );
  const dataSpan = dataMax - dataMin || 1;
  const minSpan = dataSpan * 0.02;

  const clampFrac = (v: number) => Math.max(0, Math.min(1, v));
  const toFrac = (ms: number) => (ms - dataMin) / dataSpan;
  const leftFrac = clampFrac(toFrac(viewRange.min));
  const rightFrac = clampFrac(toFrac(viewRange.max));

  // ── Sparkline geometry (shared y-scale across history + forecast) ──────────
  const values = all.map((p) => p.y);
  const yMin = values.length > 0 ? Math.min(...values) : 0;
  const yMax = values.length > 0 ? Math.max(...values) : 1;
  const ySpan = yMax - yMin || 1;
  const toXY = (p: Point) => {
    const x = ((parseDate(p.x).getTime() - dataMin) / dataSpan) * SVG_W;
    const y = SVG_H - PAD_Y - ((p.y - yMin) / ySpan) * (SVG_H - 2 * PAD_Y);
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  };
  const histLine = series.map(toXY).join(" ");
  const histArea =
    series.length > 0
      ? `0,${SVG_H} ${histLine} ${(
          ((parseDate(series[series.length - 1].x).getTime() - dataMin) /
            dataSpan) *
          SVG_W
        ).toFixed(2)},${SVG_H}`
      : "";
  const fcLine = forecastSeries.map(toXY).join(" ");
  const nowFrac = nowMs !== undefined ? clampFrac(toFrac(nowMs)) : null;

  // ── Drag state (ref to avoid re-render during drag) ────────────────────────
  const drag = useRef<{
    mode: DragMode;
    startX: number;
    start: Span;
    target: HTMLElement;
  } | null>(null);
  const containerWidth = () =>
    containerRef.current?.getBoundingClientRect().width ?? 1;

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, mode: DragMode) => {
      e.preventDefault();
      e.stopPropagation();
      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);
      drag.current = {
        mode,
        startX: e.clientX,
        start: { ...viewRange },
        target,
      };
    },
    [viewRange],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!drag.current) return;
      const deltaMs =
        ((e.clientX - drag.current.startX) / containerWidth()) * dataSpan;
      const { mode, start } = drag.current;
      const next =
        mode === "pan"
          ? pan(start, deltaMs, data)
          : mode === "resize-left"
            ? resizeLeft(start, deltaMs, data, minSpan)
            : resizeRight(start, deltaMs, data, minSpan);
      onViewRangeChange(next);
    },
    [data, dataSpan, minSpan, onViewRangeChange],
  );

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    drag.current?.target.releasePointerCapture(e.pointerId);
    drag.current = null;
  }, []);

  // ── Click empty track → recenter window there (keep width) ─────────────────
  const onTrackPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.detail > 1) return; // double-click is handled by onDoubleClick (reset)
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const frac = (e.clientX - rect.left) / (rect.width || 1);
      onViewRangeChange(recenter(viewRange, dataMin + frac * dataSpan, data));
    },
    [viewRange, dataMin, dataSpan, data, onViewRangeChange],
  );

  const handle =
    "absolute inset-y-0 z-10 flex items-center justify-center w-2 cursor-ew-resize";

  return (
    <div
      ref={containerRef}
      data-testid="brush-track"
      className="bg-muted/30 relative h-12 w-full overflow-hidden rounded-md select-none"
      onPointerDown={onTrackPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onDoubleClick={() => onReset?.()}
    >
      {/* Sparkline */}
      {series.length > 0 && (
        <svg
          viewBox={`0 0 ${SVG_W} ${SVG_H}`}
          preserveAspectRatio="none"
          className="absolute inset-0 h-full w-full"
          aria-hidden="true"
        >
          <polygon points={histArea} className="fill-zinc-300/40" />
          <polyline
            points={histLine}
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            vectorEffect="non-scaling-stroke"
            className="text-zinc-400"
          />
          {fcLine && (
            <polyline
              points={fcLine}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeDasharray="3 2"
              vectorEffect="non-scaling-stroke"
              className="text-zinc-300"
            />
          )}
        </svg>
      )}

      {/* Dim outside the window */}
      <div
        className="absolute inset-y-0 left-0 bg-zinc-900/8"
        style={{ width: `${(leftFrac * 100).toFixed(3)}%` }}
      />
      <div
        className="absolute inset-y-0 right-0 bg-zinc-900/8"
        style={{ width: `${((1 - rightFrac) * 100).toFixed(3)}%` }}
      />

      {/* Now divider */}
      {nowFrac !== null && (
        <div
          data-testid="brush-now-divider"
          className="absolute inset-y-0 z-20 w-px bg-zinc-400/70"
          style={{ left: `${(nowFrac * 100).toFixed(3)}%` }}
        />
      )}

      {/* Window */}
      <div
        data-testid="brush-window"
        className={clsx(
          "absolute inset-y-0 z-0 rounded-md border border-[var(--primary)] bg-[var(--primary)]/10",
          "cursor-grab active:cursor-grabbing",
        )}
        style={{
          left: `${(leftFrac * 100).toFixed(3)}%`,
          right: `${((1 - rightFrac) * 100).toFixed(3)}%`,
        }}
        onPointerDown={(e) => onPointerDown(e, "pan")}
      >
        <div
          data-testid="brush-handle-left"
          className={clsx(handle, "left-0")}
          onPointerDown={(e) => onPointerDown(e, "resize-left")}
        >
          <span className="h-6 w-[3px] rounded-full bg-[var(--primary)] shadow-sm" />
        </div>
        <div
          data-testid="brush-handle-right"
          className={clsx(handle, "right-0")}
          onPointerDown={(e) => onPointerDown(e, "resize-right")}
        >
          <span className="h-6 w-[3px] rounded-full bg-[var(--primary)] shadow-sm" />
        </div>
      </div>
    </div>
  );
}
